// Shared image compression for upload paths.
//
// Why this exists: the backend triggers vision analysis off the `image_data`
// (base64) field. Phone photos are typically 3-12 MB, and base64 encoding
// inflates them by ~33%, which easily blows Lambda's 6 MB sync-invocation
// payload cap — the request fails before it reaches Lambda (no log, just a
// timeout / network error on the client).
//
// The chat upload path solves this by resizing to max 1200x1200 + re-encoding
// at JPEG 0.8 quality, which gets typical phone photos to ~150-400 KB. This
// util replicates that for the other upload surfaces (routine lineup scan,
// AddEntry AI fill) so they don't keep failing on phones.

const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1200;
const QUALITY = 0.8;
// Skip compression below this — already small enough to send raw.
const COMPRESSION_THRESHOLD_BYTES = 500_000;

async function heicToJpeg(file) {
  if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
    const heic2any = (await import('heic2any')).default;
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    return new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
  }
  return file;
}

/** Resize + recompress a File or Blob to a payload-safe JPEG. Returns a Blob. */
export async function resizeAndOptimizeImage(input) {
  let file = input;
  try {
    file = await heicToJpeg(file);
  } catch (err) {
    // Fall back to the original — better to let the upload try than to block.
    console.warn('HEIC conversion failed, sending original:', err);
  }

  // Tiny files: skip the canvas roundtrip.
  if (file.size && file.size < COMPRESSION_THRESHOLD_BYTES) return file;

  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(blob || file),
        'image/jpeg',
        QUALITY,
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

/** Read a File/Blob → base64 (no data: prefix). */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Combined: resize + base64. Returns { compressedBlob, base64 }. */
export async function prepareImageForUpload(file) {
  const compressedBlob = await resizeAndOptimizeImage(file);
  const base64 = await fileToBase64(compressedBlob);
  return { compressedBlob, base64 };
}
